import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { AppShell } from '@/widgets/AppShell';
import { ProtectedRoute, CompanyProtectedRoute, AuthProvider } from '@/features/auth';
import { CompanyPlanProvider } from '@/shared/context';

// Improved lazy loading with chunk naming through webpack's magic comments
const Dashboard = lazy(() => import(/* webpackChunkName: "dashboard" */ '@/pages/Dashboard'));
// const Analisis = lazy(() => import(/* webpackChunkName: "analisis" */ '@/pages/Analisis'));
// const Finanzas = lazy(() => import(/* webpackChunkName: "finanzas" */ '@/pages/Finanzas'));
const Configuracion = lazy(() => import(/* webpackChunkName: "configuracion" */ '@/pages/Configuracion'));
// const Profile = lazy(() => import(/* webpackChunkName: "profile" */ '@/pages/Profile'));
const Login = lazy(() => import(/* webpackChunkName: "login" */ '@/pages/Login'));
const Register = lazy(() => import(/* webpackChunkName: "register" */ '@/pages/Register'));
const SendEmailCode = lazy(() => import(/* webpackChunkName: "sendEmailCode" */ '@/pages/EmailValidation/SendEmailCode'));
const VerifyEmailCode = lazy(() => import(/* webpackChunkName: "verifyEmailCode" */ '@/pages/EmailValidation/VerifyEmailCode'));
const CompleteRegistration = lazy(() => import(/* webpackChunkName: "completeRegistration" */ '@/pages/EmailValidation/CompleteRegistration'));
const CompanySetup = lazy(() => import(/* webpackChunkName: "companySetup" */ '@/pages/CompanySetup'));
const PaymentSetupPage = lazy(() => import(/* webpackChunkName: "paymentSetup" */ '@/pages/PaymentSetup').then(module => ({ default: module.PaymentSetupPage })));
const Facturas = lazy(() => import(/* webpackChunkName: "facturas" */ '@/pages/Facturas'));
const FacturaDetail = lazy(() => import(/* webpackChunkName: "facturaDetail" */ '@/pages/Facturas/FacturaDetail'));
const AuthDebug = lazy(() => import(/* webpackChunkName: "authDebug" */ '@/pages/AuthDebug'));

// Plan-based redirect component
const PlanBasedHome = lazy(() => import(/* webpackChunkName: "planBasedHome" */ '@/pages/PlanBasedHome'));

// Loading fallback component with animation
const LoadingFallback = () => (
  <Box sx={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    height: '100%', 
    p: 3,
    minHeight: '200px'
  }} data-testid="loading-spinner">
    <CircularProgress color="primary" />
  </Box>
);

// Higher-order component to wrap lazy-loaded components with Suspense
const lazyLoad = (Component: React.LazyExoticComponent<React.ComponentType<any>>) => (
  <Suspense fallback={<LoadingFallback />}>
    <Component />
  </Suspense>
);

const Router: React.FC = () => {
  return (
    <AuthProvider>
      <Routes>
        {/* Public route for login */}
        <Route path="/login" element={lazyLoad(Login)} />
        {/* Public route for registration */}
        <Route path="/register" element={lazyLoad(Register)} />
        {/* Email validation flow routes */}
        <Route path="/send-email-code" element={lazyLoad(SendEmailCode)} />
        <Route path="/verify-email-code" element={lazyLoad(VerifyEmailCode)} />
        <Route path="/complete-registration" element={lazyLoad(CompleteRegistration)} />
        {/* Debug route for auth testing */}
        <Route path="/auth-debug" element={lazyLoad(AuthDebug)} />
        
        {/* Protected route for company setup */}
        <Route path="/company-setup" element={
          <ProtectedRoute>
            {lazyLoad(CompanySetup)}
          </ProtectedRoute>
        } />

        {/* Protected route for payment setup */}
        <Route path="/payment-setup" element={
          <ProtectedRoute>
            <CompanyProtectedRoute>
              {lazyLoad(PaymentSetupPage)}
            </CompanyProtectedRoute>
          </ProtectedRoute>
        } />

        {/* Protected routes wrapped in AppShell - these require company setup */}
        <Route path="/" element={
          <ProtectedRoute>
            <CompanyProtectedRoute>
              <CompanyPlanProvider>
                <AppShell />
              </CompanyPlanProvider>
            </CompanyProtectedRoute>
          </ProtectedRoute>
        }>
          <Route index element={lazyLoad(PlanBasedHome)} />
          <Route path="dashboard" element={lazyLoad(Dashboard)} />
          {/* <Route path="analisis" element={lazyLoad(Analisis)} />
          <Route path="finanzas" element={lazyLoad(Finanzas)} /> */}
          <Route path="facturas" element={lazyLoad(Facturas)} />
          <Route path="facturas/:id" element={lazyLoad(FacturaDetail)} />
          <Route path="configuracion" element={lazyLoad(Configuracion)} />
          {/* <Route path="profile" element={lazyLoad(Profile)} /> */}
          {/* Catch-all route for 404 */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </AuthProvider>
  );
};

export default Router;
